import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';


const BusinessHeader = ({ business, onSave, onShare, isSaved }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: business?.name,
        text: business?.shortDescription,
        url: window.location?.href,
      });
    } else {
      navigator.clipboard?.writeText(window.location?.href);
      onShare();
    }
  };

  return (
    <div className="relative">
      {/* Header Image */}
      <div className="relative h-64 md:h-80 lg:h-96 overflow-hidden rounded-lg">
        <Image
          src={business?.headerImage}
          alt={`${business?.name} header image`}
          className={`w-full h-full object-cover transition-opacity duration-500 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setImageLoaded(true)}
        />
        {!imageLoaded && (
          <div className="absolute inset-0 bg-muted animate-pulse" />
        )}
        
        {/* Overlay Actions */}
        <div className="absolute top-4 right-4 flex space-x-2">
          <button
            onClick={onSave}
            className={`p-3 rounded-full backdrop-blur-sm transition-all duration-300 ${
              isSaved
                ? 'bg-destructive text-white shadow-lg'
                : 'bg-white/80 text-foreground hover:bg-white hover:scale-110'
            }`}
            aria-label={isSaved ? 'Remove from saved' : 'Save business'}
          >
            <Icon 
              name={isSaved ? 'Heart' : 'Heart'} 
              size={20} 
              className={isSaved ? 'fill-current' : ''}
            />
          </button>
          
          <button
            onClick={handleShare}
            className="p-3 rounded-full bg-white/80 backdrop-blur-sm text-foreground hover:bg-white hover:scale-110 transition-all duration-300"
            aria-label="Share business"
          >
            <Icon name="Share2" size={20} />
          </button>
        </div>
      </div>
      {/* Business Info */}
      <div className="mt-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-4 mb-4">
              {business?.logo && (
                <div className="w-16 h-16 rounded-lg overflow-hidden border-2 border-border bg-white">
                  <Image
                    src={business?.logo}
                    alt={`${business?.name} logo`}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground mb-2">
                  {business?.name}
                </h1>
                <div className="flex flex-wrap gap-2">
                  {business?.categories?.map((category, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-primary/10 text-primary text-sm font-body rounded-full"
                    >
                      {category}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Location & Contact */}
            <div className="space-y-2 text-muted-foreground">
              <div className="flex items-center space-x-2">
                <Icon name="MapPin" size={16} />
                <span className="font-body">{business?.location}</span>
              </div>
              {business?.phone && (
                <div className="flex items-center space-x-2">
                  <Icon name="Phone" size={16} />
                  <span className="font-body">{business?.phone}</span>
                </div>
              )}
              {business?.email && (
                <div className="flex items-center space-x-2">
                  <Icon name="Mail" size={16} />
                  <span className="font-body">{business?.email}</span>
                </div>
              )}
              {business?.website && (
                <div className="flex items-center space-x-2">
                  <Icon name="Globe" size={16} />
                  <a
                    href={business?.website?.startsWith('http') ? business?.website : `https://${business?.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-body text-primary hover:text-primary/80 transition-colors"
                  >
                    {business?.website?.replace(/^https?:\/\//, '')}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Rating & Reviews */}
          {business?.rating && (
            <div className="text-right">
              <div className="flex items-center space-x-1 mb-1">
                <Icon name="Star" size={16} className="text-warning fill-current" />
                <span className="font-body font-medium text-foreground">
                  {business?.rating}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {business?.reviewCount} reviews
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessHeader;